import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus, Search, Filter, Grid, List, FileText,
  MoreVertical, Edit, Copy, Trash2, Clock, User,
  Folder, ChevronDown, Sparkles, BookOpen, LogOut, Settings,
  Wand2, MessageSquare, Download, Upload, Check, Loader2
} from 'lucide-react';
import { useAuth } from '../store/authStore';
import { AIAssistant, useAIAssistant } from '../components/AIAssistant';
import { AITemplateGenerator } from '../components/AITemplateGenerator';
import { AIFloatingButton } from '../components/AIFloatingButton';
import { CommandPalette, useCommandPalette } from '../components/CommandPalette';
import { TemplateImportExport } from '../components/TemplateImportExport';
import {
  useTemplates,
  useCreateTemplate,
  useDeleteTemplate,
  type Template,
  type TemplateCategory,
  type TemplateFormData,
} from '../services/calm-law/templates';

const categories = [
  { value: 'all', label: 'All Templates', icon: Grid },
  { value: 'real-estate', label: 'Real Estate', icon: FileText },
  { value: 'business', label: 'Business', icon: Folder },
  { value: 'estate-planning', label: 'Estate Planning', icon: BookOpen },
  { value: 'contracts', label: 'Contracts', icon: FileText },
];

export function TemplateList() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'modified'>('modified');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  // Service hooks
  const { data: templates = [], isLoading } = useTemplates({
    category: selectedCategory !== 'all' ? selectedCategory as TemplateCategory : undefined,
    search: searchQuery || undefined,
  });
  const createTemplateMutation = useCreateTemplate();
  const deleteTemplateMutation = useDeleteTemplate();

  // AI Feature States
  const aiAssistant = useAIAssistant();
  const commandPalette = useCommandPalette();
  const [showTemplateGenerator, setShowTemplateGenerator] = useState(false);

  // Import/Export States
  const [showImportExport, setShowImportExport] = useState(false);
  const [importExportMode, setImportExportMode] = useState<'import' | 'export'>('export');

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Handle AI-generated template
  const handleTemplateGenerated = (content: string, variables: any[]) => {
    console.log('Template generated:', { content, variables });
    setShowTemplateGenerator(false);
    navigate('/templates/new');
  };

  // Handle template selection for bulk operations
  const toggleTemplateSelection = (id: string) => {
    setSelectedTemplates(prev =>
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };

  const selectAllTemplates = () => {
    if (selectedTemplates.length === filteredTemplates.length) {
      setSelectedTemplates([]);
    } else {
      setSelectedTemplates(filteredTemplates.map(t => t.id));
    }
  };

  // Handle export
  const handleExport = () => {
    setImportExportMode('export');
    setShowImportExport(true);
  };

  // Handle import
  const handleImport = () => {
    setImportExportMode('import');
    setShowImportExport(true);
  };

  // Handle imported templates
  const handleImportTemplates = async (importedTemplates: any[]) => {
    if (!user?.id) {
      console.error('No user logged in');
      return;
    }

    try {
      // Create each imported template
      for (const t of importedTemplates) {
        const templateData: TemplateFormData = {
          name: t.name,
          content: t.content || '',
          category: (t.category || 'other') as TemplateCategory,
          jurisdictions: t.metadata?.jurisdictions || ['WI'],
          description: t.description,
          tags: t.tags,
        };

        await createTemplateMutation.mutateAsync({
          data: templateData,
          userId: user.id,
        });
      }

      setShowImportExport(false);
    } catch (error) {
      console.error('Error importing templates:', error);
    }
  };

  // Filter and sort templates (service already handles category/search filtering)
  const filteredTemplates = [...(templates || [])].sort((a, b) => {
    if (sortBy === 'name') return a.name.localeCompare(b.name);
    return b.updatedAt.getTime() - a.updatedAt.getTime();
  });

  // Get selected template data for export
  const selectedTemplateData = (templates || [])
    .filter(t => selectedTemplates.includes(t.id))
    .map(t => ({
      id: t.id,
      name: t.name,
      category: t.category,
      content: t.content,
      metadata: {
        jurisdictions: t.jurisdictions,
        lastModified: t.updatedAt.toISOString(),
        createdBy: t.createdBy,
      },
    }));

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-primary-900 via-[#1a1f2e] to-primary-800">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-primary-700/50 bg-primary-900/50 backdrop-blur-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-400 to-accent-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary-900" />
            </div>
            <div>
              <h1 className="text-xl font-display font-semibold text-white flex items-center gap-2">
                Lawity Studio
                {isLoading && <Loader2 className="w-4 h-4 animate-spin text-primary-400" />}
              </h1>
              <p className="text-sm text-primary-400">Template Management</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Link to="/clauses" className="btn-secondary flex items-center gap-2">
            <BookOpen className="w-4 h-4" />
            Clause Library
          </Link>
          
          {/* Import/Export Buttons */}
          <button
            onClick={handleImport}
            className="flex items-center gap-2 px-3 py-2 bg-primary-700 text-primary-200 rounded-lg hover:bg-primary-600 transition-colors"
            title="Import templates"
          >
            <Upload className="w-4 h-4" />
            Import
          </button>
          
          <button
            onClick={handleExport}
            disabled={selectedTemplates.length === 0}
            className="flex items-center gap-2 px-3 py-2 bg-primary-700 text-primary-200 rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={selectedTemplates.length === 0 ? 'Select templates to export' : `Export ${selectedTemplates.length} templates`}
          >
            <Download className="w-4 h-4" />
            Export {selectedTemplates.length > 0 && `(${selectedTemplates.length})`}
          </button>
          
          {/* AI Generate Button */}
          <button
            onClick={() => setShowTemplateGenerator(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-600 text-white font-medium rounded-lg hover:from-violet-600 hover:to-purple-700 transition-all shadow-lg shadow-violet-500/25"
          >
            <Wand2 className="w-4 h-4" />
            AI Generate
          </button>
          
          <Link to="/templates/new" className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Template
          </Link>
          
          {/* User Menu */}
          <div className="relative ml-2" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center">
                <User className="w-4 h-4 text-accent-400" />
              </div>
              <span className="text-sm font-medium text-primary-200 hidden sm:block">
                {user?.name || 'User'}
              </span>
              <ChevronDown className="w-4 h-4 text-primary-400" />
            </button>
            
            {showUserMenu && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute right-0 mt-2 w-56 bg-primary-800 border border-primary-700 rounded-xl shadow-xl py-1 z-50"
              >
                <div className="px-4 py-3 border-b border-primary-700">
                  <p className="font-medium text-white">{user?.name}</p>
                  <p className="text-sm text-primary-400">{user?.email}</p>
                  {user?.role && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-accent-500/20 text-accent-400 text-xs rounded-full capitalize">
                      {user.role}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-primary-200 hover:bg-white/5"
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </button>
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-white/5"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </header>
      
      {/* Toolbar */}
      <div className="flex items-center gap-4 px-6 py-4 border-b border-primary-700/30">
        {/* Select All Checkbox */}
        <button
          onClick={selectAllTemplates}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
            selectedTemplates.length > 0
              ? 'bg-accent-500/20 text-accent-400'
              : 'text-primary-400 hover:bg-white/5'
          }`}
        >
          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
            selectedTemplates.length === filteredTemplates.length && filteredTemplates.length > 0
              ? 'bg-accent-500 border-accent-500'
              : selectedTemplates.length > 0
              ? 'bg-accent-500/50 border-accent-500'
              : 'border-primary-500'
          }`}>
            {selectedTemplates.length > 0 && <Check className="w-3 h-3 text-white" />}
          </div>
          <span className="text-sm">
            {selectedTemplates.length === 0
              ? 'Select'
              : `${selectedTemplates.length} selected`}
          </span>
        </button>

        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-10 pr-4 py-2.5 bg-primary-800/50 border border-primary-600/50 rounded-lg text-white placeholder-primary-400 focus:outline-none focus:border-accent-500/50"
          />
        </div>
        
        {/* Category filter */}
        <div className="flex items-center gap-2 bg-primary-800/30 rounded-lg p-1">
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-all ${
                selectedCategory === cat.value
                  ? 'bg-accent-500/20 text-accent-400'
                  : 'text-primary-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <cat.icon className="w-4 h-4" />
              {cat.label}
            </button>
          ))}
        </div>
        
        {/* Sort */}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'name' | 'modified')}
          className="px-3 py-2 bg-primary-800/50 border border-primary-600/50 rounded-lg text-sm text-white focus:outline-none focus:border-accent-500/50"
        >
          <option value="modified">Last Modified</option>
          <option value="name">Name</option>
        </select>
        
        {/* View mode */}
        <div className="flex items-center gap-1 bg-primary-800/30 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'grid' ? 'bg-accent-500/20 text-accent-400' : 'text-primary-400 hover:text-white'
            }`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'list' ? 'bg-accent-500/20 text-accent-400' : 'text-primary-400 hover:text-white'
            }`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* AI Generate Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
            >
              <button
                onClick={() => setShowTemplateGenerator(true)}
                className="flex flex-col items-center justify-center h-48 w-full bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-2 border-dashed border-violet-500/30 rounded-xl hover:border-violet-500/50 hover:from-violet-500/20 hover:to-purple-500/20 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mb-3">
                  <Wand2 className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-violet-400 group-hover:text-violet-300 transition-colors">
                  Generate with AI
                </span>
                <span className="text-xs text-primary-500 mt-1">
                  Describe your document
                </span>
              </button>
            </motion.div>

            {filteredTemplates.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (index + 1) * 0.05 }}
              >
                <TemplateCard 
                  template={template}
                  isSelected={selectedTemplates.includes(template.id)}
                  onSelect={() => toggleTemplateSelection(template.id)}
                />
              </motion.div>
            ))}
            
            {/* New template card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (filteredTemplates.length + 1) * 0.05 }}
            >
              <Link
                to="/templates/new"
                className="flex flex-col items-center justify-center h-48 border-2 border-dashed border-primary-600/50 rounded-xl hover:border-accent-500/50 hover:bg-accent-500/5 transition-all group"
              >
                <Plus className="w-10 h-10 text-primary-500 group-hover:text-accent-400 transition-colors" />
                <span className="mt-2 text-sm font-medium text-primary-400 group-hover:text-accent-400 transition-colors">
                  Create New Template
                </span>
              </Link>
            </motion.div>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTemplates.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <TemplateRow 
                  template={template}
                  isSelected={selectedTemplates.includes(template.id)}
                  onSelect={() => toggleTemplateSelection(template.id)}
                />
              </motion.div>
            ))}
          </div>
        )}
        
        {filteredTemplates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-primary-400">
            <FileText className="w-16 h-16 mb-4 opacity-50" />
            <p className="text-lg font-medium">No templates found</p>
            <p className="text-sm mt-1">Try adjusting your search or filters</p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowTemplateGenerator(true)}
                className="flex items-center gap-2 px-4 py-2 bg-violet-500/20 text-violet-400 rounded-lg hover:bg-violet-500/30 transition-colors"
              >
                <Wand2 className="w-4 h-4" />
                Generate with AI
              </button>
              <button
                onClick={handleImport}
                className="flex items-center gap-2 px-4 py-2 bg-primary-700 text-primary-200 rounded-lg hover:bg-primary-600 transition-colors"
              >
                <Upload className="w-4 h-4" />
                Import Templates
              </button>
            </div>
          </div>
        )}
      </div>

      {/* AI Floating Button */}
      <AIFloatingButton
        onOpenChat={() => aiAssistant.open({ currentPage: 'Templates' })}
        onOpenGenerator={() => setShowTemplateGenerator(true)}
        showAnalyzer={false}
        showSmartFill={false}
        showClauseSuggestions={false}
      />

      {/* Command Palette */}
      <CommandPalette 
        isOpen={commandPalette.isOpen} 
        onClose={commandPalette.close}
        onOpenAIAssistant={() => aiAssistant.open({ currentPage: 'Templates' })}
        onOpenTemplateGenerator={() => setShowTemplateGenerator(true)}
      />

      {/* AI Assistant Panel */}
      <AIAssistant 
        isOpen={aiAssistant.isOpen} 
        onClose={aiAssistant.close}
        context={aiAssistant.context}
      />

      {/* AI Template Generator Modal */}
      <AITemplateGenerator
        isOpen={showTemplateGenerator}
        onClose={() => setShowTemplateGenerator(false)}
        onGenerated={handleTemplateGenerated}
      />

      {/* Template Import/Export Modal */}
      <TemplateImportExport
        isOpen={showImportExport}
        onClose={() => setShowImportExport(false)}
        mode={importExportMode}
        selectedTemplates={selectedTemplateData}
        onImport={handleImportTemplates}
      />
    </div>
  );
}

interface TemplateCardProps {
  template: Template;
  isSelected?: boolean;
  onSelect?: () => void;
}

function TemplateCard({ template, isSelected, onSelect }: TemplateCardProps) {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  const categoryColors: Record<string, string> = {
    'real-estate': 'bg-blue-500/20 text-blue-400',
    'business': 'bg-green-500/20 text-green-400',
    'estate-planning': 'bg-purple-500/20 text-purple-400',
    'contracts': 'bg-orange-500/20 text-orange-400',
    'imported': 'bg-cyan-500/20 text-cyan-400',
  };

  return (
    <div
      className={`group relative p-4 bg-primary-800/30 border rounded-xl hover:bg-primary-800/50 transition-all cursor-pointer ${
        isSelected
          ? 'border-accent-500 ring-2 ring-accent-500/20'
          : 'border-primary-700/50 hover:border-accent-500/30'
      }`}
    >
      {/* Selection checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.();
        }}
        className={`absolute top-3 left-3 w-5 h-5 rounded border flex items-center justify-center transition-all ${
          isSelected
            ? 'bg-accent-500 border-accent-500'
            : 'border-primary-500 opacity-0 group-hover:opacity-100 hover:border-accent-400'
        }`}
      >
        {isSelected && <Check className="w-3 h-3 text-white" />}
      </button>

      {/* Content (clickable area) */}
      <div onClick={() => navigate(`/templates/${template.id}`)}>
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="w-10 h-10 rounded-lg bg-primary-700/50 flex items-center justify-center ml-6">
            <FileText className="w-5 h-5 text-accent-400" />
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-all"
          >
            <MoreVertical className="w-4 h-4 text-primary-400" />
          </button>
        </div>
        
        {/* Title */}
        <h3 className="font-medium text-white mb-2 line-clamp-2">
          {template.name}
        </h3>
        
        {/* Meta */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`badge ${categoryColors[template.category] || 'badge-accent'}`}>
            {template.category.replace('-', ' ')}
          </span>
          {template.jurisdictions.map(j => (
            <span key={j} className="badge badge-accent">
              {j}
            </span>
          ))}
        </div>
        
        {/* Footer */}
        <div className="flex items-center gap-2 text-xs text-primary-400">
          <Clock className="w-3 h-3" />
          <span>{template.updatedAt.toLocaleDateString()}</span>
        </div>
      </div>
      
      {/* Menu dropdown */}
      {showMenu && (
        <div
          className="absolute right-2 top-12 z-10 w-40 bg-primary-800 border border-primary-700 rounded-lg shadow-xl py-1"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-200 hover:bg-white/5">
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-200 hover:bg-white/5">
            <Copy className="w-4 h-4" />
            Duplicate
          </button>
          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary-200 hover:bg-white/5">
            <Download className="w-4 h-4" />
            Export
          </button>
          <div className="border-t border-primary-700 my-1" />
          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error-400 hover:bg-white/5">
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

interface TemplateRowProps {
  template: Template;
  isSelected?: boolean;
  onSelect?: () => void;
}

function TemplateRow({ template, isSelected, onSelect }: TemplateRowProps) {
  const navigate = useNavigate();

  const categoryColors: Record<string, string> = {
    'real-estate': 'bg-blue-500/20 text-blue-400',
    'business': 'bg-green-500/20 text-green-400',
    'estate-planning': 'bg-purple-500/20 text-purple-400',
    'contracts': 'bg-orange-500/20 text-orange-400',
    'imported': 'bg-cyan-500/20 text-cyan-400',
  };

  return (
    <div
      className={`flex items-center gap-4 p-4 bg-primary-800/30 border rounded-lg hover:bg-primary-800/50 transition-all cursor-pointer ${
        isSelected
          ? 'border-accent-500 ring-2 ring-accent-500/20'
          : 'border-primary-700/50 hover:border-accent-500/30'
      }`}
    >
      {/* Selection checkbox */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect?.();
        }}
        className={`w-5 h-5 rounded border flex items-center justify-center transition-all flex-shrink-0 ${
          isSelected
            ? 'bg-accent-500 border-accent-500'
            : 'border-primary-500 hover:border-accent-400'
        }`}
      >
        {isSelected && <Check className="w-3 h-3 text-white" />}
      </button>

      <div
        onClick={() => navigate(`/templates/${template.id}`)}
        className="flex items-center gap-4 flex-1 min-w-0"
      >
        <div className="w-10 h-10 rounded-lg bg-primary-700/50 flex items-center justify-center flex-shrink-0">
          <FileText className="w-5 h-5 text-accent-400" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-white truncate">{template.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className={`badge text-xs ${categoryColors[template.category] || 'badge-accent'}`}>
              {template.category.replace('-', ' ')}
            </span>
            {template.jurisdictions.map(j => (
              <span key={j} className="badge badge-accent text-xs">
                {j}
              </span>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-6 text-sm text-primary-400">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{template.createdBy}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{template.updatedAt.toLocaleDateString()}</span>
          </div>
        </div>
      </div>
      
      <button
        onClick={(e) => e.stopPropagation()}
        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-primary-400" />
      </button>
    </div>
  );
}
